import { useEffect, useRef, useCallback } from "react";
import { useSWR, cache } from "./useSWR.ts";
import { useCurrentMinutes } from "./useCurrentMinutes.ts";
import { usePullToRefresh } from "./usePullToRefresh.ts";
import { useToast } from "./ToastContext.tsx";
import { toMinutes, formatTime, shiftDate, todayStr } from "./utils.ts";
import { classifyClass } from "./classCategory.ts";
import type { ClassEvent } from "./classCategory.ts";

async function fetchClasses(key: string): Promise<ClassEvent[]> {
  const date = key.replace("classes/", "");
  const r = await fetch(`/api/classes?date=${date}`);
  if (r.status === 401) throw new Error("__401__");
  const data = await r.json();
  if (data?.error) throw new Error(data.error);
  return Array.isArray(data) ? data : [];
}

// Prefetch yesterday + 7 days ahead in one range request, split into SWR cache
let prefetchedFor = "";
function prefetchRange(date: string) {
  if (prefetchedFor === date) return;
  prefetchedFor = date;

  const start = shiftDate(date, -1);
  const end = shiftDate(date, 7);

  fetch(`/api/classes/range?start=${start}&end=${end}`)
    .then((r) => r.ok ? r.json() : null)
    .then((byDate: Record<string, any[]> | null) => {
      if (!byDate) return;
      for (const [d, events] of Object.entries(byDate)) {
        const key = `classes/${d}`;
        if (!cache.has(key)) {
          cache.set(key, { data: events, time: Date.now() });
        }
      }
      // Also cache empty days in the range
      const cur = new Date(start + "T12:00:00");
      const endD = new Date(end + "T12:00:00");
      while (cur <= endD) {
        const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
        const key = `classes/${ds}`;
        if (!cache.has(key)) {
          cache.set(key, { data: [], time: Date.now() });
        }
        cur.setDate(cur.getDate() + 1);
      }
    })
    .catch(() => {});
}

function classStatus(cls: ClassEvent, nowMinutes: number, isToday: boolean, isPast: boolean): string {
  if (isPast) return "class-past";
  if (!isToday) return "";
  const s = toMinutes(cls.start);
  const e = toMinutes(cls.end);
  if (nowMinutes >= e) return "class-past";
  if (nowMinutes >= s && nowMinutes < e) return "class-now";
  return "";
}

function attendanceFillClass(count: number, max: number): string {
  if (!max || max <= 0) return "fill-ok";
  const ratio = count / max;
  if (ratio >= 1) return "fill-full";
  if (ratio >= 0.8) return "fill-almost";
  return "fill-ok";
}

export function ClassList({ date, onSelect, onSessionExpired, hiddenGyms, onGymsLoaded, selectedClassId }: {
  date: string;
  onSelect: (id: string) => void;
  onSessionExpired: () => void;
  hiddenGyms: string[];
  onGymsLoaded: (gyms: string[]) => void;
  selectedClassId?: string | null;
}) {
  const { data: classes, error, isLoading, mutate } = useSWR(`classes/${date}`, fetchClasses, { refreshInterval: 30_000, initialDelay: 5_000 });
  const reportedRef = useRef<string>("");
  const nowMinutes = useCurrentMinutes();
  const today = todayStr();
  const isToday = date === today;
  const isPast = date < today;
  const { addToast } = useToast();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleRefresh = useCallback(async () => {
    const key = `classes/${date}`;
    cache.delete(key);
    try {
      await mutate();
    } catch (e: any) {
      if (e?.message !== "__401__") {
        addToast("Päivitys epäonnistui", "error");
      }
    }
  }, [date, mutate, addToast]);

  const { pullDistance, refreshing } = usePullToRefresh(containerRef, { onRefresh: handleRefresh });

  // Prefetch surrounding days after current day has loaded
  useEffect(() => {
    if (classes) prefetchRange(date);
  }, [date, !!classes]);

  if (error === "__401__") { onSessionExpired(); return null; }

  // Group by gym/room (from all classes, before filtering)
  const grouped: Record<string, ClassEvent[]> = {};
  if (classes) {
    for (const cls of classes) {
      const gym = cls.employee_names?.[0]?.service_provider_name || "Muu";
      if (!grouped[gym]) grouped[gym] = [];
      grouped[gym]!.push(cls);
    }
  }

  const allGyms = Object.keys(grouped).sort((a, b) => a.localeCompare(b, "fi"));

  // Report gym names to parent (for settings panel)
  const gymsKey = allGyms.join(",");
  useEffect(() => {
    if (gymsKey && gymsKey !== reportedRef.current) {
      reportedRef.current = gymsKey;
      onGymsLoaded(allGyms);
    }
  }, [gymsKey]);

  if (error) {
    addToast(error, "error");
    return <div className="loading">Virhe ladattaessa</div>;
  }
  if (isLoading) return <div className="loading"><div className="spinner" /></div>;
  if (!classes || classes.length === 0) return <div className="empty">Ei tunteja tänään</div>;

  const visibleGyms = allGyms.filter((g) => !hiddenGyms.includes(g));

  // Detect spanning vapaavuoro events (appear in ALL visible gyms at same time slot)
  const spanningIds = new Set<string>();
  const spanningSlots: { key: string; events: ClassEvent[] }[] = [];
  if (visibleGyms.length > 1) {
    const vapaavuoroBySlot = new Map<string, { gyms: Set<string>; events: ClassEvent[] }>();
    for (const gym of visibleGyms) {
      for (const cls of grouped[gym] || []) {
        if (classifyClass(cls.productname) !== "vapaavuoro") continue;
        const slotKey = `${cls.start}|${cls.end}`;
        let entry = vapaavuoroBySlot.get(slotKey);
        if (!entry) {
          entry = { gyms: new Set(), events: [] };
          vapaavuoroBySlot.set(slotKey, entry);
        }
        entry.gyms.add(gym);
        entry.events.push(cls);
      }
    }
    for (const [key, { gyms, events }] of vapaavuoroBySlot) {
      if (gyms.size === visibleGyms.length) {
        for (const e of events) spanningIds.add(e.id);
        spanningSlots.push({ key, events });
      }
    }
  }

  // Compute time range
  let minMin = Infinity;
  let maxMin = -Infinity;
  for (const cls of classes) {
    const s = toMinutes(cls.start);
    const e = toMinutes(cls.end);
    if (s < minMin) minMin = s;
    if (e > maxMin) maxMin = e;
  }
  const gridStart = Math.floor(minMin / 60) * 60;
  const gridEnd = Math.ceil(maxMin / 60) * 60;
  const totalMinutes = gridEnd - gridStart;

  const hours: number[] = [];
  for (let m = gridStart; m <= gridEnd; m += 60) {
    hours.push(m);
  }

  // Now line position
  const showNowLine = isToday && nowMinutes >= gridStart && nowMinutes <= gridEnd;
  const nowTop = showNowLine ? ((nowMinutes - gridStart) / totalMinutes) * 100 : 0;

  return (
    <div className="class-list" ref={containerRef}>
      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || refreshing) && (
        <div className="pull-indicator" style={{ height: pullDistance }}>
          <div className={`pull-spinner${refreshing ? " spinning" : ""}`}
            style={{ opacity: Math.min(pullDistance / 60, 1) }}
          />
        </div>
      )}
      <div className="timeline">
        {/* Gym column headers */}
        <div className="timeline-header">
          <div className="timeline-time-col" />
          {visibleGyms.map((gym) => (
            <div key={gym} className="timeline-gym-label">{gym}</div>
          ))}
        </div>

        {/* Time grid area */}
        <div className="timeline-body" style={{ "--timeline-hours": (gridEnd - gridStart) / 60 } as React.CSSProperties}>
          {/* Time labels column */}
          <div className="timeline-time-col">
            {hours.map((m) => (
              <div
                key={m}
                className="hour-label"
                style={{ top: `${((m - gridStart) / totalMinutes) * 100}%` }}
              >
                {`${Math.floor(m / 60)}:00`}
              </div>
            ))}
          </div>

          {/* Now indicator line */}
          {showNowLine && (
            <div className="now-indicator" style={{ top: `${nowTop}%` }} />
          )}

          {/* Spanning vapaavuoro cards */}
          {spanningSlots.map(({ key, events }) => {
            // Use first event for display; sum attendance across all
            const first = events[0]!;
            const s = toMinutes(first.start);
            const e = toMinutes(first.end);
            const top = ((s - gridStart) / totalMinutes) * 100;
            const height = ((e - s) / totalMinutes) * 100;
            const count = events.reduce((sum, ev) => sum + (ev.customer?.length || 0), 0);
            const max = events.reduce((sum, ev) => sum + (ev.maxparticipants || 0), 0);
            const fillClass = attendanceFillClass(count, max);
            const status = classStatus(first, nowMinutes, isToday, isPast);
            return (
              <div
                key={`span-${key}`}
                className={`class-card-spanning${status ? ` ${status}` : ""}`}
                style={{ top: `${top}%`, height: `${height}%` }}
                onClick={() => onSelect(first.id)}
              >
                <div className="name">{first.productname?.trim()}</div>
                <div className="card-meta">
                  {formatTime(first.start)}–{formatTime(first.end)}
                </div>
                <div className="card-attendance">
                  <span className={`attendance-count ${fillClass}`}>
                    {count}/{max || "?"}
                  </span>
                  {max > 0 && (
                    <div className="attendance-bar">
                      <div
                        className={`attendance-fill ${fillClass}`}
                        style={{ width: `${Math.min((count / max) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Gym columns */}
          {visibleGyms.map((gym) => (
            <div key={gym} className="timeline-col">
              {/* Hour lines */}
              {hours.map((m) => (
                <div
                  key={m}
                  className="hour-line"
                  style={{ top: `${((m - gridStart) / totalMinutes) * 100}%` }}
                />
              ))}
              {/* Class cards */}
              {grouped[gym]!.filter((cls) => !spanningIds.has(cls.id)).map((cls) => {
                const s = toMinutes(cls.start);
                const e = toMinutes(cls.end);
                const top = ((s - gridStart) / totalMinutes) * 100;
                const height = ((e - s) / totalMinutes) * 100;
                const count = cls.customer?.length || 0;
                const max = cls.maxparticipants || 0;
                const fillClass = attendanceFillClass(count, max);
                const status = classStatus(cls, nowMinutes, isToday, isPast);
                const category = classifyClass(cls.productname);
                return (
                  <div
                    key={cls.id}
                    className={`class-card${status ? ` ${status}` : ""}${cls.id === selectedClassId ? " class-selected" : ""}`}
                    style={{ top: `${top}%`, height: `${height}%`, borderLeftColor: `var(--cat-${category})` }}
                    onClick={() => onSelect(cls.id)}
                  >
                    <div className="name">{cls.productname?.trim()}</div>
                    <div className="card-meta">
                      {formatTime(cls.start)}–{formatTime(cls.end)}
                    </div>
                    <div className="card-attendance">
                      <span className={`attendance-count ${fillClass}`}>
                        {count}/{max || "?"}
                      </span>
                      {max > 0 && (
                        <div className="attendance-bar">
                          <div
                            className={`attendance-fill ${fillClass}`}
                            style={{ width: `${Math.min((count / max) * 100, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
